import React, { useState, useEffect, useRef } from 'react';
import { Building2, ChevronDown, Check, Clock, XCircle, ExternalLink } from 'lucide-react';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

const CompanySwitcher = () => {
  const [companies, setCompanies] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Get current subdomain
  const getCurrentSubdomain = () => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[0] !== 'www') {
      return parts[0];
    }
    return null;
  };

  const currentSubdomain = getCurrentSubdomain();

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/company-access/my-companies');
      setCompanies(res.data);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Only show if user has access to multiple companies
  const approvedCompanies = companies.filter(c => c.status === 'approved');
  
  if (loading || approvedCompanies.length <= 1) {
    return null;
  }

  const currentCompany = companies.find(c => c.company_subdomain === currentSubdomain);
  const pendingCount = companies.filter(c => c.status === 'pending').length;

  const handleSwitchCompany = (company) => {
    if (company.status !== 'approved') return;
    
    // Navigate to the company's subdomain
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // Get base domain (remove current subdomain)
    const parts = hostname.split('.');
    let baseDomain;
    if (parts.length > 2) {
      baseDomain = parts.slice(1).join('.');
    } else {
      baseDomain = hostname;
    }
    
    // Build new URL
    let newUrl = `${protocol}//${company.company_subdomain}.${baseDomain}`;
    if (port && port !== '80' && port !== '443') {
      newUrl += `:${port}`;
    }
    newUrl += '/admin/dashboard';

    window.location.href = newUrl;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-50 border-green-200';
      case 'pending':
        return 'bg-amber-50 border-amber-200';
      case 'rejected':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 border border-blue-200 rounded-xl transition-all"
        data-testid="company-switcher-button"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-blue-200">
            {currentCompany?.company_logo ? (
              <img 
                src={resolveFileUrl(currentCompany.company_logo, process.env.REACT_APP_BACKEND_URL)}
                alt=""
                className="w-5 h-5 object-contain"
              />
            ) : (
              <Building2 className="w-4 h-4 text-blue-600" />
            )}
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
              {currentCompany?.company_name || 'Select Company'}
            </p>
            <p className="text-xs text-gray-500">{approvedCompanies.length} companies</p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl z-50 overflow-hidden">
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2">Your Companies</p>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {companies.map((company) => (
              <button
                key={company.id}
                onClick={() => handleSwitchCompany(company)}
                disabled={company.status !== 'approved'}
                className={`w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition disabled:opacity-60 disabled:cursor-not-allowed ${
                  company.company_subdomain === currentSubdomain ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${getStatusColor(company.status)}`}>
                    {company.company_logo ? (
                      <img 
                        src={resolveFileUrl(company.company_logo, process.env.REACT_APP_BACKEND_URL)}
                        alt=""
                        className="w-5 h-5 object-contain"
                      />
                    ) : (
                      <Building2 className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-gray-900">{company.company_name}</p>
                    <p className="text-xs text-gray-500">
                      {company.is_home_company ? 'Home Company' : company.company_subdomain}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(company.status)}
                  {company.status === 'approved' && company.company_subdomain !== currentSubdomain && (
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  )}
                  {company.company_subdomain === currentSubdomain && (
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Current</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {pendingCount > 0 && (
            <div className="p-2 border-t border-gray-100 bg-amber-50">
              <p className="text-xs text-amber-700 px-2">
                {pendingCount} request{pendingCount > 1 ? 's' : ''} pending approval
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySwitcher;
